import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { api } from '../partage/api/client';
import type { DossierVu, LigneVivier } from '../partage/api/types';
import { IconeCalendrier, IconeCasesReliees, IconeEclair, IconeHorloge } from '../partage/icones';
import { cles } from '../partage/relectures';
import { useAuPouce } from '../partage/surface';
import { aujourdhui, aujourdhuiEnLettres, heureDe, joursEntre, jourCourt } from '../partage/datesEnMots';
import {
  attendDepuis,
  CA_ARRIVE,
  CE_QUI_COINCE,
  faitesSur,
  J_ATTENDS,
  LA_CARTE_DE_TA_VIE,
  majuscule,
  PROCHAINE,
  proprietesEnMots,
  retient,
  RIEN_A_FAIRE_TOUT_DE_SUITE,
  RIEN_N_ARRIVE,
  RIEN_NE_COINCE,
  AUCUNE_REPONSE_ATTENDUE,
  salutation,
  SEPT_JOURS,
  TOI,
  TU_PEUX_FAIRE_MAINTENANT,
} from '../partage/vocabulaire';
import { teinteDuProjet } from '../apparence';
import { fileDeMaintenant, REGLAGES_PAR_DEFAUT, requeteDesReglages } from '../maintenant';
import { cePassage, NouveauProjet } from '../projets';
import { BandeauAdresse } from '../comptes';
import { FormulaireALaPremiereVisite, nomDuCompte, usePrenom } from '../profil';
import { ceQuiArrive, GALETS_AVANT_DE_SERRER, placesEnCercle, troisAuPlus } from './carteDeTaVie';
import './Accueil.css';

/** Le vivier de « Maintenant » avec ses réglages par défaut, sans projet choisi. */
const REQUETE_DE_L_ACCUEIL = requeteDesReglages(REGLAGES_PAR_DEFAUT, null, false);

/** Les icônes des blocs, au trait : `partage/icones.tsx`, celles de la barre de gauche et du rail des angles. */
const ICONE = 'accueil-icone';

/**
 * L'accueil, la carte de ta vie (chantier `navigation`, 02, maquette du carnet `accueil.html`,
 * référence `design/accueil.html`) : l'ordinateur s'ouvre ici. « Bonjour » ou « Bonsoir » et la
 * date ; au centre, « Toi », et tes projets autour en galets ; à droite, quatre blocs.
 *
 * Il lit ce que les autres écrans lisent déjà, par les mêmes requêtes : les projets (« Projets »),
 * le vivier de « Maintenant » avec ses réglages par défaut, l'Agenda. Il ne recalcule rien : il
 * place les galets en cercle à l'affichage, trie et coupe à trois lignes.
 *
 * Le prénom écrit dans Profil remplace « Toi » ; un compte qui n'a pas vu le premier formulaire du
 * profil le trouve ici, par-dessus (chantier profil, ticket 03).
 *
 * Au téléphone, il n'existe pas : l'adresse ramène à « Maintenant » (décision d'Igor du 2026-10-19).
 */
export function Accueil() {
  if (useAuPouce()) return <Navigate to="/maintenant" replace />;
  return <LaCarteDeTaVie />;
}

/** L'adresse par défaut : l'accueil à l'ordinateur, « Maintenant » au téléphone. */
export function VersLaPageDAccueil() {
  return <Navigate to={useAuPouce() ? '/maintenant' : '/accueil'} replace />;
}

function LaCarteDeTaVie() {
  const naviguer = useNavigate();
  const projets = useQuery({ queryKey: cles.projets, queryFn: api.projets });
  const vivier = useQuery({
    queryKey: cles.maintenant(REQUETE_DE_L_ACCUEIL),
    queryFn: () => api.maintenant(REQUETE_DE_L_ACCUEIL),
  });
  const agenda = useQuery({ queryKey: cles.agenda, queryFn: () => api.agenda() });
  const prenom = usePrenom();

  if (projets.isPending || vivier.isPending) return <div className="accueil" />;

  /*
   * Sans projet à soi, l'accueil aussi (décision d'Igor du 2026-10-21, choix A) : « Toi » au centre
   * et l'invitation à poser le premier projet (`CarteDeTaVie`). Plus de renvoi vers « Maintenant ».
   */

  const ouvrir = (id: string) => naviguer(`/feuille/${id}`);
  const dossiers = projets.data?.dossiers ?? [];
  const v = vivier.data;
  const jour = aujourdhui();

  return (
    <div className="accueil">
      {/* Les comptes d'avant le chantier mails, tant que leur adresse n'est pas vérifiée. */}
      <BandeauAdresse />
      <header className="accueil-tete">
        <h1>{salutation(new Date().getHours())}</h1>
        <span className="accueil-date">{majuscule(aujourdhuiEnLettres())}</span>
      </header>

      <div className="accueil-corps">
        <CarteDeTaVie dossiers={dossiers} prenom={prenom} onOuvrir={ouvrir} />

        <div className="accueil-blocs">
          <Bloc titre={TU_PEUX_FAIRE_MAINTENANT} icone={<IconeEclair className={ICONE} />} vide={RIEN_A_FAIRE_TOUT_DE_SUITE} maintenant>
            {v &&
              troisAuPlus(fileDeMaintenant(v)).map((l) => (
                <Ligne
                  key={l.id}
                  titre={l.titre}
                  teinte={l.dossier?.couleur}
                  qui={proprietesEnMots(l, ['duree'])[0] ?? null}
                  onOuvrir={() => ouvrir(l.id)}
                />
              ))}
          </Bloc>

          <Bloc titre={CE_QUI_COINCE} icone={<IconeCasesReliees className={ICONE} />} vide={RIEN_NE_COINCE}>
            {v &&
              troisAuPlus(v.debloquants).map((d) => (
                <Ligne
                  key={d.bulle.id}
                  titre={d.bulle.titre}
                  teinte={d.bulle.dossier?.couleur}
                  qui={retient(d.libere.length)}
                  onOuvrir={() => ouvrir(d.bulle.id)}
                />
              ))}
          </Bloc>

          <Bloc titre={J_ATTENDS} icone={<IconeHorloge className={ICONE} />} vide={AUCUNE_REPONSE_ATTENDUE}>
            {v &&
              troisAuPlus(v.aVerifier).map((l) => (
                <Ligne
                  key={l.id}
                  titre={l.titre}
                  teinte={l.dossier?.couleur}
                  qui={depuisQuand(l, jour)}
                  monde={l.situation.nom === 'a_verifier'}
                  onOuvrir={() => ouvrir(l.id)}
                />
              ))}
          </Bloc>

          <Bloc titre={CA_ARRIVE} precision={SEPT_JOURS} icone={<IconeCalendrier className={ICONE} />} vide={RIEN_N_ARRIVE}>
            {agenda.data &&
              ceQuiArrive(agenda.data.semaines, jour).map((e) => (
                <Ligne
                  key={`${e.bulleId}|${e.nature}`}
                  titre={e.titre}
                  teinte={e.dossier?.couleur}
                  qui={e.debut ? `${majuscule(jourCourt(e.jour))} · ${heureDe(new Date(e.debut))}` : majuscule(jourCourt(e.jour))}
                  onOuvrir={() => ouvrir(e.bulleId)}
                />
              ))}
          </Bloc>
        </div>
      </div>

      <FormulaireALaPremiereVisite />
    </div>
  );
}

/** « 12 jours, à relancer » : depuis le jour où la demande est partie, compté au jour de l'utilisateur. */
function depuisQuand(ligne: LigneVivier, jour: string): string {
  const jours = ligne.dateAction ? joursEntre(ligne.dateAction, jour) : null;
  return attendDepuis(jours, ligne.situation.nom === 'a_verifier');
}

/** Au centre « Toi » (ou le prénom), autour un galet par projet, placés en cercle à l'affichage. */
function CarteDeTaVie({
  dossiers,
  prenom,
  onOuvrir,
}: {
  dossiers: readonly DossierVu[];
  prenom: string | null;
  onOuvrir: (id: string) => void;
}) {
  const places = placesEnCercle(dossiers.length);
  const serre = dossiers.length > GALETS_AVANT_DE_SERRER;

  return (
    <section className="accueil-carte" aria-label={LA_CARTE_DE_TA_VIE}>
      <div className="accueil-toi">
        <b>{nomDuCompte(prenom, TOI)}</b>
      </div>
      {dossiers.length === 0 && (
        <div className="accueil-premier">
          <p className="accueil-premier-titre">Ton carnet est vide.</p>
          <p className="accueil-premier-mot">
            Pose ton premier projet&nbsp;: une idée, un objectif, tout ce qui prend plus d&apos;une étape.
          </p>
          <NouveauProjet />
        </div>
      )}
      {dossiers.map((d, i) => {
        const place = places[i]!;
        return (
          <Galet
            key={d.id}
            dossier={d}
            serre={serre}
            style={{ left: `${place.x * 100}%`, top: `${place.y * 100}%` }}
            onOuvrir={() => onOuvrir(d.id)}
          />
        );
      })}
    </section>
  );
}

function Galet({
  dossier,
  serre,
  style,
  onOuvrir,
}: {
  dossier: DossierVu;
  serre: boolean;
  style: { left: string; top: string };
  onOuvrir: () => void;
}) {
  const { faites, total } = dossier.progression;
  // La même chose que la colonne « Maintenant » de « Projets » : la prochaine, sinon ce qui s'y passe.
  const passe = dossier.prochaine ? null : cePassage(dossier);

  return (
    <button
      type="button"
      className={`accueil-galet${serre ? ' accueil-galet--serre' : ''}`}
      data-dossier={dossier.id}
      style={{ ...style, ['--teinte' as string]: teinteDuProjet(dossier.couleur) }}
      onClick={onOuvrir}
    >
      <span className="accueil-galet-nom">
        <i className="accueil-pastille" aria-hidden="true" />
        {dossier.titre}
      </span>
      <span className="accueil-galet-faites">{faitesSur(faites, total)}</span>
      {dossier.prochaine ? (
        <span className="accueil-galet-suite">
          {PROCHAINE} : <b>{dossier.prochaine.titre}</b>
        </span>
      ) : (
        passe && (
          <span className={`accueil-galet-suite${passe.ton === 'monde' ? ' accueil-galet-suite--monde' : ''}`}>
            {passe.phrase}
          </span>
        )
      )}
    </button>
  );
}

function Bloc({
  titre,
  precision,
  icone,
  vide,
  maintenant = false,
  children,
}: {
  titre: string;
  precision?: string;
  icone: ReactNode;
  vide: string;
  maintenant?: boolean;
  children: ReactNode[] | false | undefined | null;
}) {
  const lignes = Array.isArray(children) ? children : [];
  return (
    <section className={`accueil-bloc${maintenant ? ' accueil-bloc--maintenant' : ''}`}>
      <h2>
        {icone}
        {titre}
        {precision && <small>{precision}</small>}
      </h2>
      {lignes.length === 0 ? <p className="accueil-bloc-vide">{vide}</p> : <ul>{lignes}</ul>}
    </section>
  );
}

function Ligne({
  titre,
  teinte,
  qui,
  monde = false,
  onOuvrir,
}: {
  titre: string;
  teinte: string | null | undefined;
  qui: string | null;
  monde?: boolean;
  onOuvrir: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        className="accueil-ligne"
        style={{ ['--teinte' as string]: teinteDuProjet(teinte) }}
        onClick={onOuvrir}
      >
        <span className="accueil-ligne-titre">
          <i className="accueil-pastille" aria-hidden="true" />
          {titre}
        </span>
        {qui && <span className={`accueil-ligne-qui${monde ? ' accueil-ligne-qui--monde' : ''}`}>{qui}</span>}
      </button>
    </li>
  );
}
